import { Button, IconButton, Typography } from "@suraasa/placebo-ui"
import clsx from "clsx"
import SuraasaPlusLogo, {
  LogoVariant,
} from "features/SuraasaPlus/components/SuraasaPlusLogo"
import { Xmark } from "iconoir-react"
import { useNavigate } from "react-router-dom"
import routes from "routes"
import { BROWSER_STORAGE_KEYS } from "utils/constants"
import { getPlatformURL } from "utils/helpers"

import { SpnyTourEnum, useSuraasaPlusNewYear } from "../../Context"
import { tourManager } from "../../TourManager"
import GradientBlock from "../assets/tour-gradient.webp"
import { DASHBOARD_ONBOARDING_TARGETS } from "../constants"

const ArrowRight = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      fill="#3B82F6"
      style={{ fill: "#3B82F6", fillOpacity: 1 }}
    />
    <path
      d="M8.00024 11.9998H16.0002M16.0002 11.9998L12.5002 8.49976M16.0002 11.9998L12.5002 15.4998"
      stroke="#FFF6E5"
      style={{ stroke: "#FFF6E5", strokeOpacity: 1 }}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Card = ({
  title,
  description,
  className,
  route,
  isPendingTour,
  handleClose,
  subDescription,
}: {
  title: string
  description: string
  subDescription?: string
  route: string
  className?: string
  isPendingTour?: boolean
  slug: string
  handleClose?: () => void
}) => {
  const navigation = useNavigate()

  if (isPendingTour) {
    return (
      <button
        onClick={() => {
          if (route.includes("jobs")) {
            tourManager.completed(BROWSER_STORAGE_KEYS.spnyJobs)
            window.location.href = route
          } else {
            navigation(route)
          }
          if (handleClose) handleClose()
        }}
        className={clsx(
          "group relative block rounded-xl border border-transparent bg-onSurface-100 p-2 text-start transition-all hover:border-onSurface-200 hover:bg-white",
          className
        )}
      >
        <Typography className="mb-1" variant="strongSmallBody">
          {title}
        </Typography>
        <Typography variant="smallBody" className="text-onSurface-600">
          {description}
        </Typography>
        {subDescription && (
          <Typography className="mt-0.5 text-xs font-normal leading-[130%] tracking-normal text-onSurface-500">
            {subDescription}
          </Typography>
        )}
        <div className="absolute right-2 top-2 opacity-100 transition-all group-hover:opacity-100 sm:opacity-0">
          <ArrowRight />
        </div>
      </button>
    )
  }

  return (
    <div
      className={clsx(
        "block rounded-xl border border-transparent bg-onSurface-100 p-2 text-start",
        className
      )}
    >
      <Typography className="mb-1" variant="strongSmallBody">
        {title}
      </Typography>
      <Typography variant="smallBody" className="text-onSurface-600">
        {description}
      </Typography>
      {subDescription && (
        <Typography className="mt-0.5 text-xs font-normal leading-[130%] tracking-normal text-onSurface-500">
          {subDescription}
        </Typography>
      )}
    </div>
  )
}
const Content = ({
  handleContinue,
  isPendingTour,
  handleClose,
}: {
  handleContinue: () => void
  isPendingTour?: boolean
  handleClose?: () => void
}) => {
  const {
    spnyAiToolsTour,
    spnyJobsTour,
    spnyMasterclassTour,
    spnyImpactCourseTour,
    spnyCpatDashboardTour,
  } = useSuraasaPlusNewYear()
  return (
    <div className="relative overflow-hidden rounded-t-2xl">
      {!isPendingTour && (
        <img
          src={GradientBlock}
          alt="gradient-block"
          className="absolute left-0 top-0 z-1"
        />
      )}
      <div className="relative z-2">
        <div
          className={clsx("px-3 pb-3", {
            "pt-[42px]": !isPendingTour,
            "pt-3": isPendingTour,
          })}
        >
          {!isPendingTour ? (
            <>
              <div className="mb-2">
                <Typography className="text-[32px] font-bold leading-[100%] tracking-[-4%] text-onSurface-600">
                  Welcome to
                </Typography>
                <SuraasaPlusLogo
                  variant={LogoVariant.full}
                  color="dark"
                  height={36}
                  width={201}
                />
              </div>
              <Typography className="text-onSurface-500" variant="largeBody">
                Here’s everything you’ve unlocked
              </Typography>
            </>
          ) : (
            <div className="mb-1 flex items-start justify-between gap-2">
              <Typography variant="title2" className="max-w-[284px]">
                What would you like to experience first?
              </Typography>
              <IconButton color="black" onClick={handleClose}>
                <Xmark />
              </IconButton>
            </div>
          )}
        </div>
        <div className="px-3 pb-15 sm:pb-1.5">
          {spnyCpatDashboardTour !== SpnyTourEnum.completed && (
            <Card
              handleClose={handleClose}
              isPendingTour={isPendingTour}
              title="CPAT Skill Analysis"
              className="mb-1.5"
              description={
                isPendingTour
                  ? "Understand your strengths and start strategically advancing your career."
                  : "A skill assessment that clearly defines your strengths for targeted growth."
              }
              slug={DASHBOARD_ONBOARDING_TARGETS.cpatAssessment}
              route={routes.cpat.dashboard}
            />
          )}
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 sm:grid-rows-[repeat(2,minmax(131px,1fr))]">
            {spnyAiToolsTour !== SpnyTourEnum.completed && (
              <Card
                handleClose={handleClose}
                isPendingTour={isPendingTour}
                title="Advanced AI Tools"
                description={
                  isPendingTour
                    ? "Save hours of planning and immediately take back your weekends."
                    : "11 Advanced AI Teaching Tools with unlimited generations to save you hours of time."
                }
                slug={DASHBOARD_ONBOARDING_TARGETS.aiTools}
                route={routes.aiTools.home}
              />
            )}
            {spnyJobsTour !== SpnyTourEnum.completed && (
              <Card
                handleClose={handleClose}
                isPendingTour={isPendingTour}
                title="Premium Jobs"
                description="Access to teaching jobs at top schools around the globe to plan your next career move."
                slug={DASHBOARD_ONBOARDING_TARGETS.jobs}
                route={getPlatformURL("jobs", "/")}
              />
            )}
            {spnyMasterclassTour !== SpnyTourEnum.completed && (
              <Card
                handleClose={handleClose}
                isPendingTour={isPendingTour}
                title="Expert Masterclasses"
                description={
                  isPendingTour
                    ? "Learn from global experts and get certified for your skills."
                    : "60+ expert-led, certified masterclasses to help you lead with confidence."
                }
                slug={DASHBOARD_ONBOARDING_TARGETS.masterclasses}
                route={routes.masterclasses}
              />
            )}
            {spnyImpactCourseTour !== SpnyTourEnum.completed && (
              <Card
                handleClose={handleClose}
                isPendingTour={isPendingTour}
                title="IMPACT Course"
                description={
                  isPendingTour
                    ? "Get instant confidence and make measurable difference in your classroom."
                    : "A self-paced course designed to boost your teaching skills and make a measurable difference."
                }
                subDescription={
                  isPendingTour ? "CPAT recommended first" : undefined
                }
                slug={DASHBOARD_ONBOARDING_TARGETS.learning}
                route={routes.learning}
              />
            )}
          </div>
        </div>

        <div className="fixed bottom-0 w-full border-t border-onSurface-300 bg-white px-3 pb-3 pt-2 shadow-xl sm:relative sm:border-t-0 sm:bg-transparent">
          {!isPendingTour ? (
            <Button
              onClick={handleContinue}
              color="black"
              className="ml-auto flex w-full  sm:w-fit"
            >
              Take a tour
            </Button>
          ) : (
            <Button
              onClick={handleClose}
              color="black"
              className="ml-auto flex w-full bg-onSurface-100 text-onSurface-900 sm:w-fit sm:bg-white"
            >
              Maybe Later
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Content
